import { useCallback, useEffect, useRef, useState } from "react";

export const PLAYBACK_RATES = [0.8, 1, 1.25, 1.5, 2];

export const usePodcastPlayer = ({ onDurationKnown } = {}) => {
  const audioRef = useRef(null);
  const [activeTracks, setActiveTracks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [completedKeys, setCompletedKeys] = useState(() => new Set());

  const current = currentIndex != null ? activeTracks[currentIndex] : null;
  const playbackRateRef = useRef(playbackRate);

  useEffect(() => {
    if (!current?.url || !audioRef.current) return;
    audioRef.current.load();
    audioRef.current.playbackRate = playbackRateRef.current;
    audioRef.current.play().catch(() => {});
  }, [current?.url]);

  useEffect(() => {
    playbackRateRef.current = playbackRate;
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const playList = useCallback((tracks, index = 0) => {
    if (!tracks || tracks.length === 0) return;
    const clamped = Math.min(Math.max(0, index), tracks.length - 1);
    setActiveTracks(tracks);
    setCurrentTime(0);
    setDuration(0);
    setCurrentIndex(clamped);
  }, []);

  const playAt = useCallback((index) => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex === index) {
        audioRef.current?.play().catch(() => {});
        return prevIndex;
      }
      setCurrentTime(0);
      setDuration(0);
      return index;
    });
  }, []);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) audioRef.current.play().catch(() => {});
    else audioRef.current.pause();
  }, []);

  const seek = useCallback((time) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const skip = useCallback((amount) => {
    if (!audioRef.current) return;
    const target = Math.min(Math.max(0, audioRef.current.currentTime + amount), duration || Infinity);
    audioRef.current.currentTime = target;
    setCurrentTime(target);
  }, [duration]);

  const next = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex == null) return prevIndex;
      return Math.min(activeTracks.length - 1, prevIndex + 1);
    });
  }, [activeTracks.length]);

  const prev = useCallback(() => {
    setCurrentIndex((prevIndex) => {
      if (prevIndex == null) return prevIndex;
      return Math.max(0, prevIndex - 1);
    });
  }, []);

  const moveTrack = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= activeTracks.length || fromIndex === toIndex) return;

    const reordered = [...activeTracks];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setActiveTracks(reordered);

    setCurrentIndex((prevIndex) => {
      if (prevIndex == null) return prevIndex;
      if (prevIndex === fromIndex) return toIndex;
      if (fromIndex < prevIndex && toIndex >= prevIndex) return prevIndex - 1;
      if (fromIndex > prevIndex && toIndex <= prevIndex) return prevIndex + 1;
      return prevIndex;
    });
  };

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setActiveTracks([]);
    setCurrentIndex(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  }, []);

  const removeTrack = (index) => {
    const remaining = activeTracks.filter((_, i) => i !== index);
    setActiveTracks(remaining);

    setCurrentIndex((prevIndex) => {
      if (prevIndex == null) return prevIndex;
      if (remaining.length === 0) return null;
      if (index < prevIndex) return prevIndex - 1;
      if (index === prevIndex) return Math.min(prevIndex, remaining.length - 1);
      return prevIndex;
    });
  };

  const audioEventHandlers = {
    onPlay: () => setIsPlaying(true),
    onPause: () => setIsPlaying(false),
    onTimeUpdate: () => setCurrentTime(audioRef.current?.currentTime || 0),
    onLoadedMetadata: () => {
      const value = audioRef.current?.duration || 0;
      setDuration(value);
      if (current && value > 0) onDurationKnown?.(current.key, value);
    },
    onEnded: () => {
      if (current) {
        setCompletedKeys((prevSet) => {
          const nextSet = new Set(prevSet);
          nextSet.add(current.key);
          return nextSet;
        });
      }
      setCurrentIndex((prevIndex) => {
        if (prevIndex == null || prevIndex >= activeTracks.length - 1) {
          setIsPlaying(false);
          return prevIndex;
        }
        return prevIndex + 1;
      });
    },
  };

  return {
    audioRef,
    activeTracks,
    current,
    currentIndex,
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    completedKeys,
    playList,
    playAt,
    togglePlay,
    seek,
    skip,
    setPlaybackRate,
    next,
    prev,
    stop,
    moveTrack,
    removeTrack,
    audioEventHandlers,
  };
};
